import { useMemo, useState, type CSSProperties, type JSX } from "react";
import { BowSigil } from "../components/Wordmark";
import type { Language } from "../strings";
import type { CorpusIndexShort } from "./corpus-types";
import { flechaDisplayTitle, flechaYear } from "./flechas";

interface FlechasShelfProps {
  entries: CorpusIndexShort[];
  language: Language;
  onOpen: (videoId: string) => void;
  onViewAll?: () => void;
  totalCount?: number;
  standalone?: boolean;
}

function normalize(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

export function FlechasShelf({
  entries,
  language,
  onOpen,
  onViewAll,
  totalCount,
  standalone = false,
}: FlechasShelfProps): JSX.Element | null {
  const es = language === "es";
  const [query, setQuery] = useState("");
  const visibleEntries = useMemo(() => {
    const needle = normalize(query.trim());
    if (!standalone || !needle) return entries;
    return entries.filter((entry) => normalize(entry.title ?? "").includes(needle));
  }, [entries, query, standalone]);
  const count = totalCount ?? entries.length;
  if (entries.length === 0 && !standalone) return null;
  const Heading = standalone ? "h1" : "h2";

  return (
    <section className={`flechas-shelf${standalone ? " flechas-shelf--standalone" : ""}`}>
      <header className="flechas-shelf__head">
        <div className="flechas-shelf__mark" aria-hidden="true">
          <BowSigil />
        </div>
        <div className="flechas-shelf__intro">
          <span className="flechas-shelf__kicker">
            {es ? "Preguntas prácticas" : "Practical questions"}
          </span>
          <Heading className="flechas-shelf__title">Flechas</Heading>
          <p className="flechas-shelf__sub">
            {es
              ? "Aclaraciones cortas basadas en los YouTube Shorts de Koresh."
              : "Short explanations based on Koresh's YouTube Shorts."}
          </p>
        </div>
        <div className="flechas-shelf__tools">
          <span className="flechas-shelf__count">
            {count} {count === 1 ? (es ? "aclaración" : "explanation") : es ? "aclaraciones" : "explanations"}
          </span>
          {onViewAll ? (
            <button type="button" className="flechas-shelf__all" onClick={onViewAll}>
              {es ? "Ver todas" : "View all"} <span aria-hidden="true">→</span>
            </button>
          ) : null}
        </div>
      </header>

      {standalone && entries.length > 6 ? (
        <label className="flechas-shelf__search">
          <span className="creader-sr-only">{es ? "Buscar Flechas" : "Search Flechas"}</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={es ? "Busca una pregunta o palabra…" : "Search a question or word…"}
          />
        </label>
      ) : null}

      {visibleEntries.length > 0 ? (
        <ol className="flechas-list" aria-label={es ? "Aclaraciones cortas" : "Short explanations"}>
          {visibleEntries.map((entry, index) => {
            const year = flechaYear(entry.slugs);
            return (
              <li
                key={entry.entry_id}
                className="flechas-list__item"
                style={{ "--flecha-i": Math.min(index, 8) } as CSSProperties}
              >
                <button
                  type="button"
                  className="flechas-list__button"
                  onClick={() => onOpen(entry.video_id)}
                >
                  <span className="flechas-list__shaft" aria-hidden="true">
                    <span />
                  </span>
                  <span className="flechas-list__body">
                    <span className="flechas-list__title">
                      {flechaDisplayTitle(
                        entry.title,
                        es ? "Aclaración de Koresh" : "Koresh short teaching",
                      )}
                    </span>
                    <span className="flechas-list__meta">
                      {year ?? (es ? "Enseñanza corta" : "Short teaching")}
                      <span aria-hidden="true"> · </span>
                      {entry.chunk_count === 1
                        ? es
                          ? "lectura breve"
                          : "brief read"
                        : es
                          ? `${entry.chunk_count} partes`
                          : `${entry.chunk_count} parts`}
                    </span>
                  </span>
                  <span className="flechas-list__go" aria-hidden="true">→</span>
                </button>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="flechas-shelf__empty">
          {query
            ? es
              ? "Ninguna Flecha coincide con esa búsqueda."
              : "No Flecha matches that search."
            : es
              ? "Aún no hay Flechas listas para leer."
              : "No Flechas are ready to read yet."}
        </p>
      )}
    </section>
  );
}
